import { useEffect, useMemo, useRef } from 'react';

import { useId } from '@mantine/hooks';
import { useSetAtom } from 'jotai';

import { formyAtomFamily, formyListAtom } from '../atoms';
import { UnknownForm } from '../types/form';
import { useFormyContext } from './useFormyContext';

function useFormy<T extends UnknownForm>({ form, id }: { form: T; id?: string }) {
  const formRef = useRef<HTMLDivElement>(null);
  const { store } = useFormyContext();

  const atomId = useId(id);

  // Updates the `stableFormAtom` with the form object, scopes this atom to the store
  const setItem = useSetAtom(formyAtomFamily({ id: atomId }), { store });

  //  Sets our atomId to the validatorListAtom, This list is scoped this atom to the store
  const setValidationItem = useSetAtom(formyListAtom, { store });

  const isFormDirty = useMemo(() => form.isDirty(), [form]);

  useEffect(() => {
    if (form) {
      setItem({ id: atomId, ...form, formRef });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this when the form changes
  }, [form.values, form.errors, form.validate, isFormDirty]); // Only update when values or errors change, we don't need the rest of the form updating since they're only functions

  useEffect(() => {
    // Set this item on the validator list
    if (atomId) {
      setValidationItem((prev) => [...prev, atomId]);
    }
    return () => {
      setValidationItem((prev) => prev.filter((prevId) => prevId !== atomId));
      formyAtomFamily.remove({ id: atomId });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this when the atomId changes
  }, [atomId]);

  return { id: useMemo(() => atomId, [atomId]), formRef };
}

export { useFormy };
